import React from "react";
import { Switch, Redirect, Route } from "react-router-dom";
import Login from "./Login";
import Registration from "./Registration";
import ForgotPassword from "./ForgotPassword";
import { config } from "../../../../services/config";
import { useUnitSettings } from "../../../../context/UnitSettingsContext";

export function AuthPage() {
  const { settings } = useUnitSettings();
  const unitName = settings?.unit_name || "";
  const icon = settings?.start_logo_file || "";

  const grad = `linear-gradient(160deg, ${config.sideBarBgColor} 0%, ${config.sideBarColor} 100%)`;

  return (
    <div id="auth-root" style={{ minHeight: "100vh", display: "flex", fontFamily: "'Segoe UI', Roboto, sans-serif" }}>

      {/* ── Left branding panel ── */}
      <div
        id="auth-left-panel"
        style={{
          flex: "0 0 420px",
          background: grad,
          display: "flex",
          flexDirection: "column",
          padding: "44px 48px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <div style={{ position: "absolute", top: -70, right: -70, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: 60, left: -90, width: 300, height: 300, borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -50, right: 30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />

        {/* Logo */}
        <div style={{ position: "relative", zIndex: 1, minHeight: 70 }}>
          {icon && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                background: "#fff",
                borderRadius: 12,
                padding: "10px 16px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
              }}
            >
              <img
                src={icon}
                alt="School Logo"
                style={{ maxHeight: 52, maxWidth: 170, objectFit: "contain", display: "block" }}
              />
            </div>
          )}
        </div>

        {/* Main copy */}
        <div className="auth-copy" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", position: "relative", zIndex: 1 }}>
          <p className="auth-subcopy" style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", margin: "0 0 14px" }}>
            Welcome to
          </p>
          <h1 className="auth-headline" style={{ color: "#fff", fontSize: 30, fontWeight: 800, lineHeight: 1.35, margin: "0 0 18px", wordBreak: "break-word" }}>
            {unitName || "School Management System"}
          </h1>
          <p className="auth-subcopy" style={{ color: "rgba(255,255,255,0.55)", fontSize: 14, lineHeight: 1.75, margin: 0 }}>
            A complete platform to manage your institution — students, staff, fees, and beyond.
          </p>

          {/* Feature list */}
          <div className="auth-feature-list" style={{ marginTop: 40, display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              { label: "Student & Fee Management", desc: "Admissions, challans, due tracking" },
              { label: "Staff & Payroll",           desc: "Hiring, salaries, attendance" },
              { label: "Campus Analytics",          desc: "Live dashboards & reports" },
            ].map(({ label, desc }) => (
              <div key={label} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div style={{ marginTop: 4, width: 8, height: 8, borderRadius: "50%", background: "rgba(255,255,255,0.45)", flexShrink: 0 }} />
                <div>
                  <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 14, fontWeight: 700 }}>{label}</div>
                  <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 12, marginTop: 2 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <p className="auth-footer" style={{ position: "relative", zIndex: 1, color: "rgba(255,255,255,0.35)", fontSize: 12, margin: 0 }}>
          &copy; {new Date().getFullYear()}{" "}
          <a
            href="https://tvoxel.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "rgba(255,255,255,0.6)", fontWeight: 600, textDecoration: "none" }}
          >
            Tvoxel Technologies Pvt Ltd
          </a>
        </p>
      </div>

      {/* ── Right content panel ── */}
      <div
        id="auth-right-panel"
        style={{
          flex: 1,
          background: "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
          position: "relative",
        }}
      >
        <div style={{ width: "100%", maxWidth: 440 }}>
          <Switch>
            <Route path="/auth/login" component={Login} />
            <Route path="/auth/registration" component={Registration} />
            <Route path="/auth/forgot-password" component={ForgotPassword} />
            <Redirect from="/auth" exact={true} to="/auth/login" />
            <Redirect to="/auth/login" />
          </Switch>
        </div>

        {/* Bottom copyright (mobile) */}
        <p style={{ position: "absolute", bottom: 16, left: 0, right: 0, textAlign: "center", fontSize: 12, color: "#94a3b8", margin: 0 }}>
          &copy; {new Date().getFullYear()}{" "}
          <a
            href="https://tvoxel.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#64748b", fontWeight: 600, textDecoration: "none" }}
          >
            Tvoxel Technologies Pvt Ltd
          </a>
        </p>
      </div>

      {/* Responsive */}
      <style>{`
        /* Tablet: narrow the branding panel */
        @media (max-width: 991px) {
          #auth-left-panel { flex: 0 0 320px !important; padding: 36px 32px !important; }
        }
        /* Mobile: stack to a single column, branding becomes a slim header */
        @media (max-width: 767px) {
          #auth-root { flex-direction: column !important; }
          #auth-left-panel {
            flex: 0 0 auto !important;
            padding: 28px 24px !important;
          }
          #auth-left-panel .auth-feature-list,
          #auth-left-panel .auth-footer { display: none !important; }
          #auth-left-panel .auth-copy { justify-content: flex-start !important; flex: 0 0 auto !important; }
          #auth-left-panel .auth-headline { font-size: 22px !important; margin-bottom: 0 !important; }
          #auth-left-panel .auth-subcopy { display: none !important; }
          #auth-right-panel { padding: 28px 16px 64px !important; }
        }
        @media (max-width: 400px) {
          #auth-right-panel { padding: 20px 12px 64px !important; }
        }
      `}</style>
    </div>
  );
}
